import React, { Children, cloneElement, isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './GoHoverTooltip.css';

const OFFSET = 8;
const PAD = 8;

function assignRef(ref, node) {
  if (!ref) return;
  if (typeof ref === 'function') ref(node);
  else if (typeof ref === 'object') ref.current = node;
}

function resolveBoxEl(anchor) {
  if (!anchor || typeof window === 'undefined') return anchor;
  try {
    if (window.getComputedStyle(anchor).display === 'contents') {
      return anchor.firstElementChild || anchor;
    }
  } catch {
    /* ignore */
  }
  return anchor;
}

/**
 * Tooltip branco com seta via portal (não é cortado por overflow da tabela).
 * align: 'center' | 'start' | 'end'
 * onlyWhenTruncated: se true, só abre o tip quando o conteúdo do âncora estiver truncado.
 * className com "go-hover-tip-wrap--contents": anexa o tip no filho (sem span wrapper),
 * evitando caixa fantasma na seleção DOM quando o filho usa CSS zoom.
 */
export function GoHoverTooltip({
  text,
  content,
  portalClassName = '',
  children,
  align = 'center',
  className = '',
  onlyWhenTruncated = false,
  delayMs = 0,
  wrap = false,
  zIndex,
}) {
  const hasTip = !!text || content != null;
  const hasTipRef = useRef(hasTip);
  hasTipRef.current = hasTip;
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const delayTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState(null);
  const [arrowLeft, setArrowLeft] = useState('50%');
  const [placeBelow, setPlaceBelow] = useState(false);

  const attachToChild = String(className || '').includes('go-hover-tip-wrap--contents');

  const place = useCallback(() => {
    const boxEl = resolveBoxEl(anchorRef.current);
    const tip = tipRef.current;
    if (!boxEl || !tip) return;

    const rect = boxEl.getBoundingClientRect();
    const tipW = tip.offsetWidth;
    const tipH = tip.offsetHeight;
    let left;
    if (align === 'start') left = rect.left;
    else if (align === 'end') left = rect.right - tipW;
    else left = rect.left + rect.width / 2 - tipW / 2;

    left = Math.min(Math.max(PAD, left), window.innerWidth - tipW - PAD);

    let below = false;
    let top = rect.top - tipH - OFFSET;
    if (top < PAD) {
      top = rect.bottom + OFFSET;
      below = true;
    }

    const anchorCenter = rect.left + rect.width / 2;
    const arrow = Math.min(Math.max(10, anchorCenter - left), tipW - 10);
    setArrowLeft(`${arrow}px`);
    setPlaceBelow(below);
    setStyle({ top, left });
  }, [align]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return undefined;
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place, text, content]);

  const clearDelay = useCallback(() => {
    if (delayTimerRef.current != null) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!hasTip) {
      clearDelay();
      setOpen(false);
      return undefined;
    }
    const el = resolveBoxEl(anchorRef.current);
    if (!el?.matches(':hover')) setOpen(false);
    return undefined;
  }, [hasTip, clearDelay]);

  const tryOpen = useCallback(() => {
    if (!hasTip) return;
    if (onlyWhenTruncated) {
      const anchor = resolveBoxEl(anchorRef.current);
      if (!anchor) return;
      const measureEl =
        anchor.querySelector?.('.go-dev-ellipsis') ||
        anchor.firstElementChild ||
        anchor;
      const cell = anchor.closest?.('td');
      const trunc =
        measureEl.scrollWidth > measureEl.clientWidth + 1 ||
        (cell != null && cell.scrollWidth > cell.clientWidth + 1);
      if (!trunc) return;
    }
    clearDelay();
    if (delayMs > 0) {
      delayTimerRef.current = setTimeout(() => {
        delayTimerRef.current = null;
        const el = resolveBoxEl(anchorRef.current);
        if (!hasTipRef.current || !el?.matches(':hover')) return;
        setOpen(true);
      }, delayMs);
      return;
    }
    setOpen(true);
  }, [hasTip, onlyWhenTruncated, delayMs, clearDelay]);

  const hide = useCallback(() => {
    clearDelay();
    setOpen(false);
  }, [clearDelay]);

  const portal =
    open
      ? createPortal(
          <span
            ref={tipRef}
            className={[
              'go-hover-tip-portal',
              placeBelow ? 'go-hover-tip-portal--below' : '',
              wrap ? 'go-hover-tip-portal--wrap' : '',
              portalClassName,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              ...(style || { visibility: 'hidden', top: 0, left: 0 }),
              ...(zIndex != null ? { zIndex } : null),
            }}
            role="tooltip"
          >
            <span className="go-hover-tip-arrow" style={{ left: arrowLeft }} />
            <span className="go-hover-tip-text">{content != null ? content : text}</span>
          </span>,
          document.body
        )
      : null;

  if (!hasTip) return children;

  if (attachToChild) {
    const child = Children.only(children);
    if (!isValidElement(child)) return children;

    const chainedRef = (node) => {
      anchorRef.current = node;
      assignRef(child.ref, node);
    };

    return (
      <>
        {cloneElement(child, {
          ref: chainedRef,
          onMouseEnter: (e) => {
            child.props.onMouseEnter?.(e);
            tryOpen();
          },
          onMouseLeave: (e) => {
            child.props.onMouseLeave?.(e);
            hide();
          },
          onFocus: (e) => {
            child.props.onFocus?.(e);
            tryOpen();
          },
          onBlur: (e) => {
            child.props.onBlur?.(e);
            hide();
          },
        })}
        {portal}
      </>
    );
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={['go-hover-tip-wrap', className].filter(Boolean).join(' ')}
        onMouseEnter={tryOpen}
        onMouseLeave={hide}
        onFocus={tryOpen}
        onBlur={hide}
      >
        {children}
      </span>
      {portal}
    </>
  );
}
